/**
 * ==========================================================================
 * FloraHut Modular Testimonials Slider Web Component
 * File: assets/section-testimonials.js
 * Description: Vanilla JS, ultra-fast, zero-dependency Web Component
 *              for high-performance testimonial carousel navigation.
 * ==========================================================================
 */

if (!customElements.get('fh-testimonials-slider')) {
  class FhTestimonialsSlider extends HTMLElement {
    constructor() {
      super();
      this.track = null;
      this.slides = [];
      this.prevBtn = null;
      this.nextBtn = null;
      this.dots = [];
      this.autoplayTimer = null;
      this.isAutoplayEnabled = false;
      this.autoplaySpeed = 5000;
      this.activeSlideIndex = 0;
      this.boundHandleScroll = this.throttle(this.handleScroll.bind(this), 50);
    }

    connectedCallback() {
      this.track = this.querySelector('.fh-testimonials__track');
      if (!this.track) return;

      this.slides = Array.from(this.querySelectorAll('.fh-testimonials__slide'));
      if (this.slides.length === 0) return;

      this.prevBtn = this.querySelector('.fh-testimonials__arrow--prev');
      this.nextBtn = this.querySelector('.fh-testimonials__arrow--next');
      this.dots = Array.from(this.querySelectorAll('.fh-testimonials__dot'));

      this.isAutoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(this.dataset.speed, 10) || 5000;

      this.initEventListeners();
      this.initIntersectionObserver();
      this.initResizeObserver();
      this.updateArrowsState();

      if (this.isAutoplayEnabled) {
        this.startAutoplay();
      }

      // Shopify Theme Editor listener
      document.addEventListener('shopify:block:select', this.onBlockSelect.bind(this));
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.track) {
        this.track.removeEventListener('scroll', this.boundHandleScroll);
      }
      if (this.slideObserver) {
        this.slideObserver.disconnect();
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      document.removeEventListener('shopify:block:select', this.onBlockSelect.bind(this));
    }

    initResizeObserver() {
      if (!('ResizeObserver' in window) || !this.track) return;
      this.resizeObserver = new ResizeObserver(() => {
        this.updateArrowsState();
      });
      this.resizeObserver.observe(this.track);
    }

    initEventListeners() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.scrollPrev());
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.scrollNext());
      }

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.scrollToIndex(index));
      });

      this.track.addEventListener('scroll', this.boundHandleScroll, { passive: true });

      // Keyboard navigation
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          this.scrollPrev();
        } else if (e.key === 'ArrowRight') {
          this.scrollNext();
        }
      });

      // Pause autoplay on user interaction
      if (this.isAutoplayEnabled) {
        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => this.startAutoplay());
        this.addEventListener('focusin', () => this.stopAutoplay());
        this.addEventListener('focusout', () => this.startAutoplay());
        this.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true });
        this.addEventListener('touchend', () => this.startAutoplay(), { passive: true });
      }
    }

    initIntersectionObserver() {
      if (!('IntersectionObserver' in window)) return;

      const options = {
        root: this.track,
        threshold: 0.6
      };

      this.slideObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = this.slides.indexOf(entry.target);
            if (index !== -1) {
              this.activeSlideIndex = index;
              this.updateActiveDot(index);
            }
          }
        });
      }, options);

      this.slides.forEach((slide) => this.slideObserver.observe(slide));
    }

    scrollPrev() {
      if (!this.track || this.slides.length === 0) return;
      const slideWidth = this.getSlideStep();
      this.track.scrollBy({ left: -slideWidth, behavior: 'smooth' });
    }

    scrollNext() {
      if (!this.track || this.slides.length === 0) return;
      const slideWidth = this.getSlideStep();
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;

      // Loop back to start if at the end during autoplay
      if (this.track.scrollLeft >= maxScroll - 10) {
        this.track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        this.track.scrollBy({ left: slideWidth, behavior: 'smooth' });
      }
    }

    scrollToIndex(index) {
      if (!this.slides[index]) return;
      const targetSlide = this.slides[index];
      const leftOffset = targetSlide.offsetLeft - this.track.offsetLeft;
      this.track.scrollTo({ left: leftOffset, behavior: 'smooth' });
    }

    getSlideStep() {
      if (!this.slides[0]) return 320;
      const firstSlide = this.slides[0];
      const secondSlide = this.slides[1];
      if (secondSlide) {
        return secondSlide.offsetLeft - firstSlide.offsetLeft;
      }
      return firstSlide.offsetWidth + 20;
    }

    handleScroll() {
      this.updateArrowsState();
    }

    updateArrowsState() {
      if (!this.track) return;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const isScrollable = maxScroll > 10;
      this.classList.toggle('is-scrollable', isScrollable);

      const isStart = this.track.scrollLeft <= 5;
      const isEnd = this.track.scrollLeft >= maxScroll - 5;

      if (this.prevBtn) {
        this.prevBtn.disabled = isStart;
        this.prevBtn.setAttribute('aria-disabled', isStart ? 'true' : 'false');
      }

      if (this.nextBtn) {
        // If autoplay is enabled, don't disable next button so users/script can loop
        if (!this.isAutoplayEnabled) {
          this.nextBtn.disabled = isEnd;
          this.nextBtn.setAttribute('aria-disabled', isEnd ? 'true' : 'false');
        } else {
          this.nextBtn.disabled = false;
          this.nextBtn.setAttribute('aria-disabled', 'false');
        }
      }
    }

    updateActiveDot(index) {
      if (this.dots.length === 0) return;
      this.dots.forEach((dot, i) => {
        const isActive = i === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    startAutoplay() {
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        this.scrollNext();
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    onBlockSelect(event) {
      if (event.detail && event.detail.blockId) {
        const matchingSlide = this.querySelector(`[data-block-id="${event.detail.blockId}"]`);
        if (matchingSlide) {
          const index = this.slides.indexOf(matchingSlide);
          if (index !== -1) {
            this.scrollToIndex(index);
          }
        }
      }
    }

    throttle(fn, wait) {
      let time = Date.now();
      return function (...args) {
        if ((time + wait - Date.now()) < 0) {
          fn(...args);
          time = Date.now();
        }
      };
    }
  }

  customElements.define('fh-testimonials-slider', FhTestimonialsSlider);
}
